// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Activity from "./activity.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "parent_id": {
      "column": {
        "default": null,
        "name": "parent_id",
        "null": false,
        "type": "uuid"
      },
      "name": "parent_id",
      "selected_by_default": null,
      "translated": null
    },
    "sub_activity_type": {
      "column": {
        "default": null,
        "name": "sub_activity_type",
        "null": false,
        "type": "string"
      },
      "name": "sub_activity_type",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "SubActivity",
  "collectionKey": "sub_activities",
  "collectionName": "sub_activities",
  "i18nKey": "sub_activity",
  "camelizedLower": "subActivity",
  "name": "SubActivity",
  "nameDasherized": "sub-activity",
  "pluralName": "sub_activities",
  "ransackable_associations": [
    {
      "className": "Activity",
      "collectionName": "activities",
      "foreignKey": "child_id",
      "name": "child",
      "macro": "belongs_to",
      "resource_name": "Activity",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Activity",
      "collectionName": "activities",
      "foreignKey": "parent_id",
      "name": "parent",
      "macro": "belongs_to",
      "resource_name": "Activity",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "child_id",
      "column": {
        "default": null,
        "name": "child_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_child_id",
      "column": {
        "default": null,
        "name": "old_child_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_parent_id",
      "column": {
        "default": null,
        "name": "old_parent_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "parent_id",
      "column": {
        "default": null,
        "name": "parent_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "sub_activity_type",
      "column": {
        "default": null,
        "name": "sub_activity_type",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Activity",
      "collectionName": "activities",
      "foreignKey": "child_id",
      "name": "child",
      "macro": "belongs_to",
      "resource_name": "Activity",
      "through": null
    },
    {
      "className": "Activity",
      "collectionName": "activities",
      "foreignKey": "parent_id",
      "name": "parent",
      "macro": "belongs_to",
      "resource_name": "Activity",
      "through": null
    }
  ],
  "paramKey": "sub_activity",
  "primaryKey": "id"
}

/** Frontend model for SubActivity. */
export default class SubActivity extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  parentId() {
    return this.readAttributeUnderscore("parent_id")
  }

  /** @returns {boolean} */
  hasParentId() {
    const value = this.parentId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  subActivityType() {
    return this.readAttributeUnderscore("sub_activity_type")
  }

  /** @returns {boolean} */
  hasSubActivityType() {
    const value = this.subActivityType()

    return this._isPresent(value)
  }

  /** @returns {import("./activity.js").default | null} */
  child() {
    return this._readBelongsToReflection({modelClass: Activity, reflectionName: "child"})
  }

  /** @returns {Promise<import("./activity.js").default | null>} */
  loadChild() {
    if (!("childId" in this)) throw new Error("Foreign key method wasn't defined: childId")

    const id = this.childId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "child",
        model: this,
        modelClass: Activity
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./activity.js").default | null>} */
  ensureChildLoaded() {
    return this.ensureAssociationLoaded("child")
  }

  /** @returns {import("./activity.js").default | null} */
  parent() {
    return this._readBelongsToReflection({modelClass: Activity, reflectionName: "parent"})
  }

  /** @returns {Promise<import("./activity.js").default | null>} */
  loadParent() {
    if (!("parentId" in this)) throw new Error("Foreign key method wasn't defined: parentId")

    const id = this.parentId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "parent",
        model: this,
        modelClass: Activity
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./activity.js").default | null>} */
  ensureParentLoaded() {
    return this.ensureAssociationLoaded("parent")
  }
}
